import * as THREE from 'three'
import type {
  SpatialCapsule,
  SpatialCollisionHit,
} from '@sharefe/share3d-engine'

const DOWN = new THREE.Vector3(0, 0, -1)
const EPSILON = 1e-5

export interface ThrownBallQueries {
  overlapSphere(sphere: THREE.Sphere): SpatialCollisionHit | null
}

export interface ThrownBallPoolOptions {
  maxBalls?: number
  radius?: number
  launchSpeed?: number
  gravity?: number
  restitution?: number
  drag?: number
  minSpeed?: number
  maxLifetime?: number
}

export interface ThrownBallSnapshot {
  activeCount: number
}

interface BallEntry {
  active: boolean
  age: number
  position: THREE.Vector3
  velocity: THREE.Vector3
  mesh: THREE.Mesh
}

const DEFAULT_OPTIONS: Required<ThrownBallPoolOptions> = {
  maxBalls: 18,
  radius: 0.16,
  launchSpeed: 12,
  gravity: 18,
  restitution: 0.72,
  drag: 0.995,
  minSpeed: 0.15,
  maxLifetime: 16,
}

export class ThrownBallPool {
  private readonly options: Required<ThrownBallPoolOptions>
  private readonly geometry: THREE.SphereGeometry
  private readonly material: THREE.MeshBasicMaterial
  private readonly balls: BallEntry[]

  constructor(
    private readonly root: THREE.Object3D,
    private readonly queries: ThrownBallQueries,
    options: ThrownBallPoolOptions = {},
  ) {
    this.options = {
      ...DEFAULT_OPTIONS,
      ...options,
    }
    this.geometry = new THREE.SphereGeometry(this.options.radius, 20, 16)
    this.material = new THREE.MeshBasicMaterial({
      color: '#ffb347',
      toneMapped: false,
    })
    this.balls = Array.from({ length: this.options.maxBalls }, () =>
      this.createBall(),
    )
  }

  throw(
    origin: THREE.Vector3,
    direction: THREE.Vector3,
    inheritVelocity = new THREE.Vector3(),
  ): void {
    const entry = this.balls.find((ball) => !ball.active) ?? this.balls[0]
    if (!entry) {
      return
    }

    const normalizedDirection =
      direction.lengthSq() > EPSILON ?
        direction.clone().normalize() :
        new THREE.Vector3(0, 1, 0)

    entry.active = true
    entry.age = 0
    entry.position
      .copy(origin)
      .addScaledVector(
        normalizedDirection,
        this.options.radius * 3,
      )
    entry.velocity.copy(inheritVelocity).addScaledVector(
      normalizedDirection,
      this.options.launchSpeed,
    )
    entry.mesh.position.copy(entry.position)
    entry.mesh.visible = true
  }

  clear(): void {
    for (const ball of this.balls) {
      this.deactivate(ball)
    }
  }

  update(delta: number, playerCapsule?: SpatialCapsule | null): void {
    const clampedDelta = Math.min(delta, 0.05)
    const steps = Math.max(1, Math.min(4, Math.ceil(clampedDelta / (1 / 60))))
    const stepDelta = clampedDelta / steps

    for (const ball of this.balls) {
      if (!ball.active) {
        continue
      }

      ball.age += clampedDelta
      if (ball.age > this.options.maxLifetime) {
        this.deactivate(ball)
        continue
      }

      for (let step = 0; step < steps; step += 1) {
        if (!ball.active) {
          break
        }

        ball.velocity.addScaledVector(DOWN, this.options.gravity * stepDelta)
        ball.position.addScaledVector(ball.velocity, stepDelta)
        this.resolvePlayerCollision(ball, playerCapsule ?? null)
        this.resolveWorldCollision(ball)
        ball.velocity.multiplyScalar(this.options.drag)

        if (ball.position.z < -100) {
          this.deactivate(ball)
          break
        }
      }

      if (!ball.active) {
        continue
      }

      if (ball.velocity.length() < this.options.minSpeed && ball.position.z < -50) {
        this.deactivate(ball)
        continue
      }

      ball.mesh.position.copy(ball.position)
    }
  }

  getSnapshot(): ThrownBallSnapshot {
    return {
      activeCount: this.balls.filter((ball) => ball.active).length,
    }
  }

  dispose(): void {
    this.clear()
    for (const ball of this.balls) {
      ball.mesh.removeFromParent()
    }
    this.geometry.dispose()
    this.material.dispose()
  }

  private createBall(): BallEntry {
    const mesh = new THREE.Mesh(this.geometry, this.material)
    mesh.visible = false
    mesh.castShadow = false
    mesh.receiveShadow = false
    mesh.frustumCulled = false
    this.root.add(mesh)

    return {
      active: false,
      age: 0,
      position: new THREE.Vector3(),
      velocity: new THREE.Vector3(),
      mesh,
    }
  }

  private resolveWorldCollision(ball: BallEntry): void {
    const hit = this.queries.overlapSphere(
      new THREE.Sphere(ball.position.clone(), this.options.radius),
    )
    if (!hit) {
      return
    }

    const normal = normalizeHitNormal(hit)
    if (!normal) {
      return
    }

    const penetrationDepth = Math.max(hit.penetrationDepth ?? 0, 0)
    if (penetrationDepth > EPSILON) {
      ball.position.addScaledVector(normal, penetrationDepth)
    }

    const into = ball.velocity.dot(normal)
    if (into < 0) {
      ball.velocity.addScaledVector(
        normal,
        -(1 + this.options.restitution) * into,
      )
    }
  }

  private resolvePlayerCollision(
    ball: BallEntry,
    playerCapsule: SpatialCapsule | null,
  ): void {
    if (!playerCapsule) {
      return
    }

    const closestPoint = closestPointOnSegment(
      ball.position,
      playerCapsule.start,
      playerCapsule.end,
    )
    const radiusSum = this.options.radius + playerCapsule.radius
    const offset = ball.position.clone().sub(closestPoint)
    const distance = offset.length()
    if (distance >= radiusSum) {
      return
    }

    const normal =
      distance > EPSILON ?
        offset.multiplyScalar(1 / distance) :
        new THREE.Vector3(0, 1, 0)
    const penetrationDepth = radiusSum - distance
    ball.position.addScaledVector(normal, penetrationDepth)

    const into = ball.velocity.dot(normal)
    if (into < 0) {
      ball.velocity.addScaledVector(
        normal,
        -(1 + this.options.restitution) * into,
      )
    }
  }

  private deactivate(ball: BallEntry): void {
    ball.active = false
    ball.age = 0
    ball.velocity.set(0, 0, 0)
    ball.mesh.visible = false
  }
}

function normalizeHitNormal(hit: SpatialCollisionHit): THREE.Vector3 | null {
  const source = hit.separationNormal ?? hit.normal
  if (!source || source.lengthSq() <= EPSILON) {
    return null
  }

  return source.clone().normalize()
}

function closestPointOnSegment(
  point: THREE.Vector3,
  start: THREE.Vector3,
  end: THREE.Vector3,
): THREE.Vector3 {
  const segment = end.clone().sub(start)
  const lengthSquared = segment.lengthSq()
  if (lengthSquared <= EPSILON) {
    return start.clone()
  }

  const t = THREE.MathUtils.clamp(
    point.clone().sub(start).dot(segment) / lengthSquared,
    0,
    1,
  )
  return start.clone().addScaledVector(segment, t)
}
